import {otsBaseApi} from "../../ots-api";
import {GeofenceEvent} from "../../../model/geofence-event";
import {removeUndefined} from "../../../util/util";
import {driverActivityCacheUpdate} from "../driver-activity/driver-activity-store";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["GeofenceEvents", "DriverActivity"]});

export const geofenceEventApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGeofenceEvent: builder.query<GeofenceEvent, {
      id: string,
      database?: string | undefined
    }>({
      query: ({id, database}) => ({
        url: `/geofence-events/${id}`,
        method: "GET",
        params: removeUndefined({
          serviceLookup: database
        })
      }),
      providesTags: (result, error, {id}) => [{type: "GeofenceEvents", id}]
    }),
    createGeofenceEvent: builder.mutation<GeofenceEvent, Partial<GeofenceEvent>>({
      query: (mapping) => ({
        url: "/geofence-events",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [
        {type: "GeofenceEvents", id: "LIST"},
        {type: "DriverActivity"},
      ]
    }),
    updateGeofenceEvent: builder.mutation<GeofenceEvent, Partial<GeofenceEvent>>({
      query: (mapping) => ({
        url: "/geofence-events",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "GeofenceEvents", id},
      ],
      async onQueryStarted(_arg, {dispatch, queryFulfilled, getState}) {
        try {
          const { data } = await queryFulfilled
          driverActivityCacheUpdate(dispatch, getState(), (draft) => {
            // The visit inside its row, not the row itself.
            const row = draft.find(s => s.geofenceEvent?.id === data.id)
            if (row) {
              row.geofenceEvent = {...row.geofenceEvent, ...data}
            }
          })
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    /** Merges a driver's back-to-back geofence visits into one; the first visit's site is kept. */
    mergeGeofenceEvents: builder.mutation<GeofenceEvent, GeofenceEvent[]>({
      query: (events) => ({
        url: "/geofence-events/merge",
        method: "POST",
        body: events,
      }),
      invalidatesTags: [{type: "GeofenceEvents", id: "LIST"}, {type: "DriverActivity"}],
    }),
    deleteGeofenceEvent: builder.mutation<void, Partial<GeofenceEvent>>({
      query: (mapping) => ({
        url: "/geofence-events",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "GeofenceEvents", id},
      ],
      async onQueryStarted({id}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled
          driverActivityCacheUpdate(dispatch, getState(), (draft) => {
            const index = draft.findIndex(s => s.geofenceEvent?.id === id)
            if (index >= 0) {
              draft.splice(index, 1)
            }
          })
        } catch (error) {
          console.debug(error)
        }
      },
    })
  }),
  overrideExisting: false
});

export const {
  useGetGeofenceEventQuery,
  useCreateGeofenceEventMutation,
  useUpdateGeofenceEventMutation,
  useDeleteGeofenceEventMutation,
  useMergeGeofenceEventsMutation,
} = geofenceEventApi;
